import { Winston } from 'winston';
import { UserWorkoutSession } from '../../entities/workoutsession';
import { IWorkoutRepository } from '../../persistence';

export class WorkoutOverlapConflictHandling {
  public static readonly maxWorkoutTimeMinutes = 24 * 60; // 24 hours

  constructor(
    private readonly workoutRepository: IWorkoutRepository,
    private readonly logger: Winston,
  ) {}

  public async findOverlapWinnerAndLosers(userId: string, workout: UserWorkoutSession)
    : Promise<{ winner: UserWorkoutSession, losers: UserWorkoutSession[] }> {
    const overlappingWorkouts = await this.findSavedWorkoutsThatOverlaps(userId, workout);
    const overlapDetected = overlappingWorkouts.length > 0;
    if (overlapDetected) {
      overlappingWorkouts.push(workout); // add requested one to overlapping set
      const winner = this.findWinnerAmongOverlappingWorkouts(overlappingWorkouts);
      const losers = overlappingWorkouts.filter((overlappingWorkout) => overlappingWorkout !== winner);
      return { winner, losers };
    }
    return { winner: workout, losers: [] }; // just return the workout if no overlap detected
  }

  public filterOutOverlappingWorkouts(workouts: UserWorkoutSession[]): UserWorkoutSession[] {
    const nonOverlappingWorkouts: UserWorkoutSession[] = [];
    // todo sort by priority, so that best workout is first
    for (const workout of workouts) {
      const hasOverlapWithValidWorkouts = nonOverlappingWorkouts.some((w) =>
        this.isOverlappingSignificantly(workout, w));
      if (!hasOverlapWithValidWorkouts) {
        nonOverlappingWorkouts.push(workout);
      } else {
        this.logger.warn('Found overlapping workout in import. Ignoring', {
          workout,
        });
      }
    }
    return nonOverlappingWorkouts;
  }

  private async findSavedWorkoutsThatOverlaps(
    userId: string,
    workoutToCheckForOverlap: UserWorkoutSession,
  ): Promise<UserWorkoutSession[]> {
    const endTimeFrom = workoutToCheckForOverlap.startTimeOfFirstExercise;
    const endTimeMax = new Date(
      endTimeFrom.getTime() + WorkoutOverlapConflictHandling.maxWorkoutTimeMinutes * 60 * 1000,
    );

    const workoutsThatEndedInTimeRange = await this.workoutRepository.getForUserThatEndedInTimeRange(
      endTimeFrom,
      endTimeMax,
      userId,
    );
    const completedWorkoutsThatCouldOverlap = workoutsThatEndedInTimeRange.filter((workout) =>
      workout.workoutMarkedAsCompletedAt !== false
      && workout.workoutMarkedAsCompletedAt !== undefined);

    const overlappingWorkouts = completedWorkoutsThatCouldOverlap.filter((possibleOverlappingWorkout) =>
      this.isOverlappingSignificantly(possibleOverlappingWorkout, workoutToCheckForOverlap));

    return overlappingWorkouts;
  }

  private isOverlappingSignificantly(a: UserWorkoutSession, b: UserWorkoutSession): boolean {
    const significantOverlapPercentage = 0.5; // eg 0.5 means 50% overlap of both
    const aStart = a.startTimeOfFirstExercise;
    const bStart = b.startTimeOfFirstExercise;
    // Check if there's any overlap first
    if (!(aStart < b.endTime && a.endTime > bStart)) {
      return false;
    }

    // Calculate the overlap duration
    const overlapStart = new Date(Math.max(aStart.getTime(), bStart.getTime()));
    const overlapEnd = new Date(Math.min(a.endTime.getTime(), b.endTime.getTime()));
    const overlapDuration = overlapEnd.getTime() - overlapStart.getTime();

    // Calculate the duration of each session
    const aDuration = a.endTime.getTime() - aStart.getTime();
    const bDuration = b.endTime.getTime() - bStart.getTime();

    // Check if the overlap is more than half of each session's duration
    const aOverlapRatio = overlapDuration / aDuration;
    const bOverlapRatio = overlapDuration / bDuration;

    return aOverlapRatio > significantOverlapPercentage
    && bOverlapRatio > significantOverlapPercentage;
  }

  private findWinnerAmongOverlappingWorkouts(
    overlappingWorkouts: UserWorkoutSession[],
  ): UserWorkoutSession {
    if (overlappingWorkouts.length < 2) {
      throw new Error('Expected at least 2 overlapping workouts be passed in');
    }

    // Sort workouts by priority and return the first (highest priority) one
    const overlappingWorkoutsSortedByPriority = overlappingWorkouts.sort((a, b) => {
      const aHasEffortPoints = UserWorkoutSession.hasEffortPoints(a);
      const bHasEffortPoints = UserWorkoutSession.hasEffortPoints(b);

      // Priority 1: Workouts with effort points are better
      if (aHasEffortPoints && !bHasEffortPoints) {
        return -1;
      }
      if (!aHasEffortPoints && bHasEffortPoints) {
        return 1;
      }

      // Priority 2: Higher effort points is better (when both have effort points)
      if (aHasEffortPoints && bHasEffortPoints) {
        const aEffortPoints = a.performanceMetrics!.load;
        const bEffortPoints = b.performanceMetrics!.load;
        return bEffortPoints - aEffortPoints;
      }

      // Priority 3: Longer duration is better
      const aDuration = UserWorkoutSession.calculateWorkoutDurationInMinutes(a.startTimeOfFirstExercise, a.endTime);
      const bDuration = UserWorkoutSession.calculateWorkoutDurationInMinutes(b.startTimeOfFirstExercise, b.endTime);
      return bDuration - aDuration; // Descending order (longer duration first)
    });

    const highestPriorityWorkout = overlappingWorkoutsSortedByPriority[0];
    return highestPriorityWorkout;
  }
}
